/**
 * Keeping track of voicemails
 */

import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useSettingsStore } from '@/stores/Settings'
import { useRealtimeStore } from '@/stores/Realtime'
import { useEmployeesStore } from '@/stores/Employees'
import { usePhonebookStore } from '@/stores/Phonebook'
import { useApiStore } from '@/stores/Api'

import logger from '@/utilities/Logger'
import globalEvents from '@/utilities/GlobalEvents'

import type { PbxVoicemailItem, PbxVoicemailListResponse } from '@/TcxApi/PbxVoicemail'

export const useVoicemailStore = defineStore('voicemails', () => {
	logger.debug('Voicemails: Initializing store')

	const realtime = useRealtimeStore()
	const ss = useSettingsStore()
	const employees = useEmployeesStore()
	const phonebook = usePhonebookStore()
	const api = useApiStore()

	/** List of voicemails */
	const voicemails = ref<PbxVoicemailItem[]>([])
	const newMessages = ref(0)
	const oldMessages = ref(0)

	const hasNewMessages = computed(() => newMessages.value > 0)

	realtime.pbxEvents.on('VOICEMAIL_NEW', event => {
		if (event._id === ss.auth?.employee) {
			newMessages.value = event.newMessages
			oldMessages.value = event.oldMessages
			const newVoicemail: PbxVoicemailItem = {
				_id: event.voicemailId,
				customer: event.customer,
				employee: event._id,
				date: new Date(),
				length: event.length,
				old: false,
				aNumber: event.callerNumber,
				aName: event.callerName
			}
			if (/^\d{1,7}$/.test(event.callerNumber)) {
				const emp = employees.findEmployeeFromNumber(event.callerNumber)
				if (emp) {
					newVoicemail.employee = emp._id
					newVoicemail.aName = emp.name
				}
			} else {
				const name = phonebook.findNameFromNumber(event.callerNumber)
				if (name) {
					newVoicemail.aName = name
				}
			}
			voicemails.value.push(newVoicemail)
			globalEvents.emit('Voicemail:Count', { newMessages: newMessages.value, oldMessages: oldMessages.value })
			logger.info(`Voicemails: New voicemail ${event.voicemailId} received`)
		}
	})

	realtime.pbxEvents.on('VOICEMAIL_DELETED', event => {
		if (event._id === ss.auth?.employee) {
			const index = voicemails.value.findIndex(voicemail => voicemail._id === event.voicemailId)
			if (index !== -1) {
				voicemails.value.splice(index, 1)
				logger.info(`Voicemails: Voicemail ${event.voicemailId} deleted`)
			}
			newMessages.value = event.newMessages
			oldMessages.value = event.oldMessages
			globalEvents.emit('Voicemail:Count', { newMessages: newMessages.value, oldMessages: oldMessages.value })
		}
	})

	realtime.pbxEvents.on('VOICEMAIL_OLD', event => {
		if (event._id === ss.auth?.employee) {
			const voicemail = voicemails.value.find(voicemail => voicemail._id === event.voicemailId)
			if (voicemail) {
				voicemail.old = true
			}
			newMessages.value = event.newMessages
			oldMessages.value = event.oldMessages
			globalEvents.emit('Voicemail:Count', { newMessages: newMessages.value, oldMessages: oldMessages.value })
			logger.info(`Voicemails: Voicemail ${event.voicemailId} marked as old`)
		}
	})

	realtime.realtimeEvents.on('Connected', () => { init() })

	if (realtime.connected) {
		init()
	}

	async function init() {
		// Load voicemails on startup and reconnect
		await ss.ready()

		try {
			await employees.ready()
			await phonebook.ready()
			const res = await api.get<PbxVoicemailListResponse>('/pbx/voicemail?offset=0&limit=1000')

			for (const vm of res.data.voicemails) {
				vm.date = new Date(vm.date)
				if (!vm.aName) {
					const emp = employees.findEmployeeFromNumber(vm.aNumber)
					if (emp) {
						vm.aName = emp.name
					}
				}
				const name = phonebook.findNameFromNumber(vm.aNumber)
				if (name) {
					vm.aName = name
				}
			}
			voicemails.value = res.data.voicemails
			newMessages.value = res.data.voicemails.filter(vm => !vm.old).length
			oldMessages.value = res.data.voicemails.filter(vm => vm.old).length
			globalEvents.emit('Voicemail:Count', { newMessages: newMessages.value, oldMessages: oldMessages.value })
			logger.info(`Voicemails: Initialized - currently we hold ${voicemails.value.length} voicemails`)
		}
		catch (_) { /**	Ignore */ }
	}

	/**
	 * Mark a voicemail as read
	 * @param id Id of voicemail to mark as read
	 * @returns True on success, false on failure
	 */
	async function markAsRead(id: string) {
		try {
			await api.get(`/pbx/voicemail/${id}/read`)
			const voicemail = voicemails.value.find(voicemail => voicemail._id === id)
			if (voicemail) {
				voicemail.old = true
				newMessages.value--
				oldMessages.value++
			}
			logger.info(`Voicemails: Voicemail ${id} marked as read`)
			return true
		}
		catch (err) {
			logger.error(`Voicemails: Failed to mark voicemail ${id} as read: ${(err as Error).message}`)
			return false
		}
	}

	/**
	 * Delete a voicemail
	 * @param id Id of voicmail to delete
	 * @returns True on success, false on failure
	 */
	async function del(id: string) {
		try {
			await api.del(`/pbx/voicemail/${id}`)
			const index = voicemails.value.findIndex(voicemail => voicemail._id === id)
			if (index !== -1) {
				voicemails.value.splice(index, 1)
			}
			newMessages.value = voicemails.value.filter(vm => !vm.old).length
			oldMessages.value = voicemails.value.filter(vm => vm.old).length
			logger.info(`Voicemails: Voicemail ${id} deleted`)
			return true
		}
		catch (err) {
			logger.error(`Voicemails: Failed to delete voicemail ${id}: ${(err as Error).message}`)
			return false
		}
	}

	/**
	 * Delete all voicemails
	 * @returns True on success, false on failure
	 */
	async function delAll() {
		try {
			await api.del('/pbx/voicemail')
			voicemails.value = []
			newMessages.value = 0
			oldMessages.value = 0
			logger.info('Voicemails: All voicemails deleted')
			return true
		}
		catch (err) {
			logger.error(`Voicemails: Failed to delete all voicemails: ${(err as Error).message}`)
			return false
		}
	}

	/**
	 * Send a voicemail to an e-mail address
	 * @param id Id of the voicemail
	 * @param email E-mail address to send the voicemail to
	 * @returns True on success, false on failure
	 */
	async function sendToEmail(id: string, email: string) {
		try {
			await api.get(`/pbx/voicemail/${id}/email?email=${encodeURIComponent(email)}`)
			logger.info(`Voicemails: Voicemail ${id} sent to email ${email}`)
			return true
		}
		catch (err) {
			logger.error(`Voicemails: Failed to send voicemail ${id} to email ${email}: ${(err as Error).message}`)
			return false
		}
	}

	return { voicemails, markAsRead, del, delAll, sendToEmail, newMessages, oldMessages, hasNewMessages }
})
